import { LogIn, LogOut, User } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useDispatch } from "react-redux";

import OtpResumeLogin from "@/D2C/features/auth/components/otp/OtpResumeLogin";
import { App_Url_New } from "@/shared/common/static";
import CommonLogoutModal from "@/shared/components/CommonLogoutModal";
import { UserAccountMenu } from "@/shared/components/UserAccountMenu";

import { closeModal, openModal } from "@/shared/redux/reducers/uiSlice";
import { usePosterReducers } from "@/shared/redux/store/useGetData";

type MenuAction = {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  className?: string;
  disabled?: boolean;
};

type HeaderAuthControlProps = {
  className?: string;
  loginButtonClassName?: string;
  extraActions?: MenuAction[];
};

export default function HeaderAuthControl({
  className,
  loginButtonClassName,
  extraActions = [],
}: HeaderAuthControlProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    auth: { customerAuth },
  } = usePosterReducers();

  const displayName =
    `${customerAuth?.client?.first_name || ""} ${customerAuth?.client?.last_name || ""}`.trim();
  const handleLogout = () => {
    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.LOGOUT_CONFIRM,
        title: "Logout?",
        className: "max-w-md",
        children: (
          <CommonLogoutModal displayName={displayName} redirectTo={App_Url_New.D2CPageUrl.home} />
        ),
      })
    );
  };

  const menuActions: MenuAction[] = [
    {
      label: "Profile",
      icon: <User className="h-4 w-4" />,
      onClick: () => navigate(App_Url_New.CommonPageUrl.profile),
    },
    ...extraActions.filter((action) => action.label.trim().toLowerCase() !== "settings"),
    {
      label: "Logout",
      icon: <LogOut className="h-4 w-4" />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const handleLoginClick = () => {
    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.AUTH_RETAIL_LOGIN,
        className: "max-w-lg",
        children: (
          <OtpResumeLogin
            isOpen
            onClose={() => dispatch(closeModal())}
            showUserLoginForm
            validationType="auto"
            autoRedirect={false}
            renderInline
          />
        ),
      })
    );
  };

  if (!customerAuth?.access_token) {
    if (location.pathname.includes("policy")) {
      return null;
    }

    return (
      <div className={className}>
        <button
          type="button"
          onClick={handleLoginClick}
          className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 font-semibold leading-none transition-colors ${
            loginButtonClassName || "bg-theme-accent text-theme-text hover:bg-theme-accent-hover"
          }`}
        >
          <span>Login</span>
          <LogIn size={18} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className={`relative flex justify-end ${className ?? ""}`}>
      <UserAccountMenu
        fullName={displayName}
        email={customerAuth.client?.email}
        actions={menuActions}
      />
    </div>
  );
}
